import { clinicianProfiles } from '../data/clinicianProfiles';
import { PUBLISHED_FEES, OVERALL_RANGE } from '../data/fees';
import { PUBLISHED_NETWORKS } from '../data/insurance';

export const SITE_URL = 'https://peacelovewellness.co';
export const ORG_ID = `${SITE_URL}/#org`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const PRACTICE_NAME = 'Peace Love Wellness Mental Health Counseling PLLC';
export const PRACTICE_BRAND_NAME = 'Peace Love Wellness';
// New filename on purpose: /images/* is cached for a year, so a replaced logo needs a new URL.
export const PRACTICE_LOGO_URL = `${SITE_URL}/images/brand/plw-icon-512.png`;
export const DEFAULT_SOCIAL_IMAGE_ALT = 'Peace Love Wellness logo';
// Set to the build date in gatsby-config.js for production builds; the fixed
// fallback keeps develop stable.
export const NON_BLOG_DATE_MODIFIED = process.env.GATSBY_BUILD_DATE || '2026-06-24';
export const PRACTICE_DESCRIPTION =
  'Peace Love Wellness is a New York online therapy practice offering relational, trauma-informed care for adults and couples who want therapy that feels clear, human, and deeper than coping skills alone.';
export const PRACTICE_CONTACT_EMAIL = 'contact@peacelovewellness.co';
export const PRACTICE_PHONE = '+1-212-221-6100';
export const PRACTICE_GOOGLE_BUSINESS_CID = '799645804220394979';
export const PRACTICE_GOOGLE_MAPS_URL = `https://www.google.com/maps?cid=${PRACTICE_GOOGLE_BUSINESS_CID}`;
export const PRACTICE_GEO = {
  latitude: 40.745307,
  longitude: -73.9939371,
};
export const PRACTICE_SAME_AS = [
  'https://www.facebook.com/peacelovewellnesstherapy',
  'https://www.instagram.com/peacelovewellness_/',
  'https://www.linkedin.com/company/peace-love-wellness/',
  'https://www.tiktok.com/@peacelovewellness',
  'https://www.zocdoc.com/practice/peace-love-wellness-112681',
  // Group-practice profile (1777198). Cameron's individual counselor profile
  // (965745) lives in his Person sameAs in clinicianProfiles.js — do not swap
  // them back: the org node must claim the practice listing, not a person's.
  'https://www.psychologytoday.com/us/therapists/peace-love-wellness-new-york-ny/1777198',
  'https://www.zencare.co/practice/peace-love-wellness',
  PRACTICE_GOOGLE_MAPS_URL,
];
export const PRACTICE_PRIMARY_PAGES = [
  { name: 'About Peace Love Wellness', url: `${SITE_URL}/about` },
  { name: 'Our Approach to Therapy in New York', url: `${SITE_URL}/our-approach` },
  { name: 'Individual Therapy in New York', url: `${SITE_URL}/individual-therapy` },
  { name: 'Couples and Relationship Therapy in New York', url: `${SITE_URL}/couples-relationship-therapy` },
  { name: 'Online Therapy Across New York', url: `${SITE_URL}/online-therapy` },
  { name: 'Work With a Supervised Clinician in New York', url: `${SITE_URL}/start-therapy-sooner` },
  { name: 'Culturally Responsive Therapy in New York', url: `${SITE_URL}/culturally-responsive-therapy` },
  { name: 'LGBTQIA+ Affirming Therapy in New York', url: `${SITE_URL}/lgbtqia-affirming-therapy` },
  { name: 'ENM, Poly and Open-Relationship Therapy in New York', url: `${SITE_URL}/enm-poly-therapy` },
  { name: 'How We Help in New York', url: `${SITE_URL}/how-we-help` },
  { name: 'Insurance and Fees', url: `${SITE_URL}/insurance-fees` },
  { name: 'Our Clinicians', url: `${SITE_URL}/our-clinicians` },
  { name: 'Therapy FAQ', url: `${SITE_URL}/faq` },
  { name: 'Get Started', url: `${SITE_URL}/get-started` },
];
// Derived from the clinicians' in-network plans (src/data/insurance.js).
export const PRACTICE_INSURANCE_NETWORKS = PUBLISHED_NETWORKS;
export const PRACTICE_KNOWS_ABOUT = [
  'Relational therapy',
  'Trauma-informed therapy',
  'EMDR therapy',
  'Dialectical behavior therapy',
  'Therapy beyond coping skills',
  'Protective strategies',
  'Nervous system adaptations',
  'Attachment patterns',
  'Individual therapy',
  'Couples and relationship therapy',
  'Online therapy',
  'Online therapy in New York',
  'Anxiety therapy',
  'Trauma therapy',
  'Burnout therapy',
  'Life transitions',
  'Relationship patterns',
  'Relationship anxiety',
  'Communication in relationships',
  'Attachment and attachment wounds',
  'Identity-affirming care',
  'LGBTQIA+ affirming therapy',
  'Polyamory-affirming therapy',
  'Ethically non-monogamous relationships',
  'Culturally responsive therapy',
  'Bicultural therapy',
  'Gender-affirming therapy',
  'Therapist matching',
  'Therapists accepting insurance in New York',
  'Therapists taking new clients in New York',
];
export const PRACTICE_KEYWORDS =
  'trauma-informed therapy, relational therapy, online therapy, New York therapist, anxiety therapy, trauma therapy, EMDR therapy, DBT therapy, couples therapy, relationship therapy, LGBTQIA+ affirming therapy, insurance-friendly therapy, therapist matching';

const NEW_YORK_STATE = { '@type': 'State', name: 'New York' };
const NEW_YORK_AUDIENCE = {
  '@type': 'Audience',
  geographicArea: NEW_YORK_STATE,
};

export const THERAPY_SERVICE_TYPES = ['Service', 'MedicalTherapy', 'PsychologicalTreatment'];

const KNOWS_ABOUT_SAME_AS = {
  'adhd': 'https://en.wikipedia.org/wiki/Attention_deficit_hyperactivity_disorder',
  'adolescents': 'https://en.wikipedia.org/wiki/Adolescence',
  'affective neuroscience': 'https://en.wikipedia.org/wiki/Affective_neuroscience',
  'anger': 'https://en.wikipedia.org/wiki/Anger',
  'anxiety': 'https://en.wikipedia.org/wiki/Anxiety',
  'attachment and attachment wounds': 'https://en.wikipedia.org/wiki/Attachment_theory',
  'attachment patterns': 'https://en.wikipedia.org/wiki/Attachment_theory',
  'attachment wounds': 'https://en.wikipedia.org/wiki/Attachment_theory',
  'autism': 'https://en.wikipedia.org/wiki/Autism_spectrum',
  'bicultural identity': 'https://en.wikipedia.org/wiki/Bicultural_identity',
  'burnout': 'https://en.wikipedia.org/wiki/Occupational_burnout',
  'cognitive behavioral therapy': 'https://en.wikipedia.org/wiki/Cognitive_behavioral_therapy',
  'cbt': 'https://en.wikipedia.org/wiki/Cognitive_behavioral_therapy',
  'cbt-informed support': 'https://en.wikipedia.org/wiki/Cognitive_behavioral_therapy',
  'client-centered therapy': 'https://en.wikipedia.org/wiki/Person-centered_therapy',
  'co-occurring concerns': 'https://en.wikipedia.org/wiki/Comorbidity',
  'couples and relationship therapy': 'https://en.wikipedia.org/wiki/Couples_therapy',
  'couples therapy': 'https://en.wikipedia.org/wiki/Couples_therapy',
  'creative arts therapy': 'https://en.wikipedia.org/wiki/Creative_arts_therapies',
  'culturally responsive care': 'https://en.wikipedia.org/wiki/Culturally_sensitive_therapy',
  'culturally responsive therapy': 'https://en.wikipedia.org/wiki/Culturally_sensitive_therapy',
  'depression': 'https://en.wikipedia.org/wiki/Depression_(mood)',
  'dialectical behavior therapy': 'https://en.wikipedia.org/wiki/Dialectical_behavior_therapy',
  'dbt': 'https://en.wikipedia.org/wiki/Dialectical_behavior_therapy',
  'dbt-informed skill-building': 'https://en.wikipedia.org/wiki/Dialectical_behavior_therapy',
  'internal family systems': 'https://en.wikipedia.org/wiki/Internal_Family_Systems_Model',
  'ifs-informed support': 'https://en.wikipedia.org/wiki/Internal_Family_Systems_Model',
  'emdr': 'https://en.wikipedia.org/wiki/Eye_movement_desensitization_and_reprocessing',
  'emdr therapy': 'https://en.wikipedia.org/wiki/Eye_movement_desensitization_and_reprocessing',
  'eye movement desensitization and reprocessing': 'https://en.wikipedia.org/wiki/Eye_movement_desensitization_and_reprocessing',
  'emotional dysregulation': 'https://en.wikipedia.org/wiki/Emotional_dysregulation',
  'emotional regulation': 'https://en.wikipedia.org/wiki/Emotional_self-regulation',
  'family expectations': 'https://en.wikipedia.org/wiki/Family',
  'fertility concerns': 'https://en.wikipedia.org/wiki/Infertility',
  'gender-diverse clients': 'https://en.wikipedia.org/wiki/Gender_diversity',
  'grief and loss': 'https://en.wikipedia.org/wiki/Grief',
  'health anxiety': 'https://en.wikipedia.org/wiki/Hypochondriasis',
  'high-achiever stress': 'https://en.wikipedia.org/wiki/Occupational_stress',
  'identity-affirming care': 'https://en.wikipedia.org/wiki/LGBT-affirmative_psychotherapy',
  'lgbtqia+ affirming care': 'https://en.wikipedia.org/wiki/LGBT-affirmative_psychotherapy',
  'lgbtqia+ affirming therapy': 'https://en.wikipedia.org/wiki/LGBT-affirmative_psychotherapy',
  'neurodivergence': 'https://en.wikipedia.org/wiki/Neurodiversity',
  'panic attacks': 'https://en.wikipedia.org/wiki/Panic_attack',
  'perfectionism': 'https://en.wikipedia.org/wiki/Perfectionism_(psychology)',
  'perinatal mental health': 'https://en.wikipedia.org/wiki/Perinatal_depression',
  'person-centered therapy': 'https://en.wikipedia.org/wiki/Person-centered_therapy',
  'polyamory-affirming therapy': 'https://en.wikipedia.org/wiki/Polyamory',
  'postpartum mental health': 'https://en.wikipedia.org/wiki/Postpartum_depression',
  'psychosis': 'https://en.wikipedia.org/wiki/Psychosis',
  'queer and trans clients': 'https://en.wikipedia.org/wiki/LGBT-affirmative_psychotherapy',
  'relationship therapy': 'https://en.wikipedia.org/wiki/Couples_therapy',
  'relational therapy': 'https://en.wikipedia.org/wiki/Relational_psychotherapy',
  'religious trauma': 'https://en.wikipedia.org/wiki/Religious_trauma_syndrome',
  'self-doubt': 'https://en.wikipedia.org/wiki/Self-doubt',
  'self-esteem': 'https://en.wikipedia.org/wiki/Self-esteem',
  'serious mental illness': 'https://en.wikipedia.org/wiki/Severe_mental_disorder',
  'substance use recovery': 'https://en.wikipedia.org/wiki/Substance_use_disorder',
  'trauma': 'https://en.wikipedia.org/wiki/Psychological_trauma',
  'trauma therapy': 'https://en.wikipedia.org/wiki/Psychological_trauma',
  'trauma-informed care': 'https://en.wikipedia.org/wiki/Trauma-informed_care',
  'trauma-informed therapy': 'https://en.wikipedia.org/wiki/Trauma-informed_care',
};

export const PRACTICE_SERVICE_CATALOG = [
  {
    name: 'Individual Therapy in New York',
    url: `${SITE_URL}/individual-therapy`,
    serviceType: ['Individual therapy', 'Online therapy', 'Trauma-informed therapy'],
    description:
      'Relational, trauma-informed individual therapy in New York for anxiety, overwhelm, trauma, burnout, self-doubt, and repeating patterns.',
    // Lowest sliding-scale rate to highest full fee (licensed); from data/fees.js.
    price: { minPrice: PUBLISHED_FEES.licensedIndividual.sliding[0], maxPrice: PUBLISHED_FEES.licensedIndividual.full[1] },
  },
  {
    name: 'Couples & Relationship Therapy in New York',
    url: `${SITE_URL}/couples-relationship-therapy`,
    serviceType: ['Couples therapy', 'Relationship therapy', 'Online therapy'],
    description:
      'Online couples and relationship therapy in New York for communication struggles, trust concerns, disconnection, and recurring relational patterns.',
    price: { minPrice: PUBLISHED_FEES.licensedCouples.sliding[0], maxPrice: PUBLISHED_FEES.licensedCouples.full[1] },
  },
  {
    name: 'Online Therapy Across New York',
    url: `${SITE_URL}/online-therapy`,
    serviceType: ['Online therapy', 'Telehealth psychotherapy'],
    description: 'Telehealth psychotherapy for adults and couples across New York.',
  },
  {
    name: 'Work With a Supervised Clinician in New York',
    url: `${SITE_URL}/start-therapy-sooner`,
    serviceType: ['Supervised clinician', 'Affordable therapy', 'Insurance-friendly therapy access'],
    description:
      'High-quality, lower-cost therapy in New York with a supervised clinician practicing under close clinical supervision.',
    price: {
      minPrice: Math.min(PUBLISHED_FEES.supervisedIndividual.sliding[0], PUBLISHED_FEES.supervisedCouples.sliding[0]),
      maxPrice: Math.max(PUBLISHED_FEES.supervisedIndividual.full[1], PUBLISHED_FEES.supervisedCouples.full[1]),
    },
  },
  {
    name: 'Culturally Responsive Therapy in New York',
    url: `${SITE_URL}/culturally-responsive-therapy`,
    serviceType: ['Culturally responsive therapy', 'Identity-affirming therapy', 'Online therapy'],
    description:
      'Culturally responsive, identity-affirming therapy in New York for clients navigating culture, family, belonging, and lived experience.',
  },
  {
    name: 'LGBTQIA+ Affirming Therapy in New York',
    url: `${SITE_URL}/lgbtqia-affirming-therapy`,
    serviceType: ['LGBTQIA+ affirming therapy', 'Gender-affirming therapy', 'Online therapy'],
    description:
      'Affirming therapy in New York for LGBTQIA+ clients, including queer, trans, nonbinary, and questioning clients.',
  },
  {
    name: 'ENM, Poly and Open-Relationship Therapy in New York',
    url: `${SITE_URL}/enm-poly-therapy`,
    serviceType: ['ENM therapy', 'Polyamory-affirming therapy', 'Relationship therapy'],
    description:
      'Poly-friendly, ENM-affirming therapy in New York for non-monogamous relationships, partners, and individuals.',
  },
  {
    name: 'Anxiety Therapy in New York',
    url: `${SITE_URL}/anxiety-therapy-new-york`,
    serviceType: ['Anxiety therapy', 'Individual therapy', 'Online therapy'],
    description:
      'Therapy for anxiety, overthinking, emotional overwhelm, and the patterns underneath them.',
  },
  {
    name: 'Trauma-Informed Therapy in New York',
    url: `${SITE_URL}/trauma-therapy-new-york`,
    serviceType: ['Trauma-informed therapy', 'Trauma therapy', 'Individual therapy', 'Online therapy'],
    description:
      'Trauma-informed therapy for adults carrying more than they show, working at the level of the nervous system, the body, and relationship patterns.',
  },
  {
    name: 'EMDR Therapy in New York',
    url: `${SITE_URL}/emdr-therapy-new-york`,
    serviceType: ['EMDR therapy', 'Trauma-focused therapy', 'Individual therapy', 'Online therapy'],
    description:
      'EMDR therapy in New York for adults whose insight has not become relief, offered inside a relational, trauma-informed approach and paced to the nervous system.',
  },
  {
    name: 'DBT Therapy in New York',
    url: `${SITE_URL}/dbt-therapy-new-york`,
    serviceType: ['DBT therapy', 'Dialectical behavior therapy', 'Individual therapy', 'Online therapy'],
    description:
      'DBT therapy in New York — dialectical behavior therapy skills for emotion regulation, distress tolerance, interpersonal effectiveness, and mindfulness, taught within individual therapy.',
  },
  {
    name: 'Burnout Therapy in New York',
    url: `${SITE_URL}/burnout-therapy-new-york`,
    serviceType: ['Burnout therapy', 'Individual therapy', 'Online therapy'],
    description:
      'Therapy for burnout, chronic exhaustion, and the patterns that drove the pace, more than stress management alone.',
  },
  {
    name: 'Couples Therapy for Communication Issues in New York',
    url: `${SITE_URL}/couples-therapy-communication-new-york`,
    serviceType: ['Couples therapy', 'Relationship therapy', 'Communication therapy'],
    description:
      'Couples therapy for recurring arguments, communication breakdown, and the cycles that keep repeating.',
  },
  {
    name: 'LGBTQIA+ Couples Therapy in New York',
    url: `${SITE_URL}/lgbtq-couples-therapy-new-york`,
    serviceType: ['LGBTQIA+ affirming therapy', 'Couples therapy', 'Relationship therapy'],
    description:
      'Affirming couples therapy for queer relationships, holding identity, minority stress, and relational dynamics with nuance and respect.',
  },
  {
    name: 'Polyamory-Affirming Relationship Therapy in New York',
    url: `${SITE_URL}/polyamory-affirming-couples-therapy-new-york`,
    serviceType: ['Polyamory-affirming therapy', 'Relationship therapy', 'Couples therapy'],
    description:
      'Relationship therapy for ENM and CNM relationships, including support for jealousy, communication, agreements, and non-monogamous dynamics.',
  },
  {
    name: 'Relationship Anxiety Therapy in New York',
    url: `${SITE_URL}/relationship-anxiety-therapy-new-york`,
    serviceType: ['Relationship anxiety therapy', 'Individual therapy', 'Online therapy'],
    description:
      'Therapy for relationship anxiety, reassurance-seeking, and fear of abandonment that strains connection.',
  },
  {
    name: 'Neurodivergent-Affirming Therapy in New York',
    url: `${SITE_URL}/neurodivergent-affirming-therapy-new-york`,
    serviceType: ['Neurodivergent-affirming therapy', 'ADHD therapy', 'Individual therapy', 'Online therapy'],
    description:
      'Neurodivergent-affirming therapy for ADHD, autistic, and late-identified adults, working with how your brain actually functions rather than against it.',
  },
  {
    name: 'Grief & Loss Therapy in New York',
    url: `${SITE_URL}/grief-therapy-new-york`,
    serviceType: ['Grief and loss therapy', 'Bereavement therapy', 'Individual therapy', 'Online therapy'],
    description:
      'Grief therapy for loss that does not follow a timeline, including death, breakups, and the disenfranchised losses no one names.',
  },
  {
    name: 'Depression Therapy in New York',
    url: `${SITE_URL}/depression-therapy-new-york`,
    serviceType: ['Depression therapy', 'Individual therapy', 'Online therapy'],
    description:
      'Therapy for depression, low mood, numbness, and the heaviness underneath, looking beneath the symptoms rather than only managing them.',
  },
  {
    name: 'Gender-Affirming Therapy in New York',
    url: `${SITE_URL}/gender-affirming-therapy-new-york`,
    serviceType: ['Gender-affirming therapy', 'LGBTQIA+ affirming therapy', 'Individual therapy', 'Online therapy'],
    description:
      'Affirming therapy for trans, nonbinary, and gender-expansive adults, including exploration, transition support, and minority stress, without gatekeeping.',
  },
  {
    name: 'Spanish-Speaking Therapy in New York',
    url: `${SITE_URL}/spanish-speaking-therapy-new-york`,
    serviceType: ['Spanish-speaking therapy', 'Bilingual therapy', 'Individual therapy', 'Online therapy'],
    description:
      'Therapy in Spanish (terapia en español) for adults in New York who would rather not do therapy in a second language, with culturally attuned care.',
  },
  {
    name: 'Bilingual & Bicultural Therapy in New York',
    url: `${SITE_URL}/bilingual-therapy-new-york`,
    serviceType: ['Bilingual therapy', 'Bicultural therapy', 'Individual therapy', 'Online therapy'],
    description:
      'Therapy for bilingual and bicultural adults navigating code-switching, family expectations, and the experience of living between two worlds.',
  },
  {
    name: 'Therapy for First-Generation Adults in New York',
    url: `${SITE_URL}/first-generation-therapy-new-york`,
    serviceType: ['First-generation therapy', 'Culturally responsive therapy', 'Individual therapy', 'Online therapy'],
    description:
      'Therapy for first-generation and immigrant-family adults navigating family obligation, guilt, achievement pressure, and survivor’s guilt.',
  },
  {
    name: 'Life Transitions Therapy in New York',
    url: `${SITE_URL}/life-transitions-therapy-new-york`,
    serviceType: ['Life transitions therapy', 'Individual therapy', 'Online therapy'],
    description:
      'Therapy for life transitions and the disorienting in-between of major change, including moves, breakups, new roles, and shifting identity.',
  },
  {
    name: 'OCD Therapy in New York',
    url: `${SITE_URL}/ocd-therapy-new-york`,
    serviceType: ['OCD therapy', 'Anxiety therapy', 'Individual therapy', 'Online therapy'],
    description:
      'Therapy for OCD, intrusive thoughts, and compulsions, including Pure O, relationship OCD, harm OCD, and scrupulosity, beyond the common stereotype.',
  },
  {
    name: 'Religious Trauma Therapy in New York',
    url: `${SITE_URL}/religious-trauma-therapy-new-york`,
    serviceType: ['Religious trauma therapy', 'Trauma-informed therapy', 'Individual therapy', 'Online therapy'],
    description:
      'Affirming, non-judgmental therapy for religious trauma, spiritual abuse, and deconstruction, healing harm from high-control religion.',
  },
  {
    name: 'Perinatal & Postpartum Therapy in New York',
    url: `${SITE_URL}/perinatal-postpartum-therapy-new-york`,
    serviceType: ['Perinatal therapy', 'Postpartum therapy', 'Maternal mental health', 'Individual therapy', 'Online therapy'],
    description:
      'Therapy for pregnancy, postpartum, and new parenthood, including postpartum depression and anxiety, birth trauma, matrescence, and pregnancy loss.',
  },
  {
    name: 'Self-Esteem Therapy in New York',
    url: `${SITE_URL}/self-esteem-therapy-new-york`,
    serviceType: ['Self-esteem therapy', 'Individual therapy', 'Online therapy'],
    description:
      'Therapy for low self-worth, the inner critic, perfectionism, and people-pleasing, working beneath affirmations to where worth felt conditional.',
  },
];

function buildTelehealthChannel(serviceUrl) {
  return {
    '@type': 'ServiceChannel',
    serviceUrl,
    availableLanguage: ['English', 'Spanish'],
  };
}

export function buildTherapyServiceSchema({
  pageUrl,
  name,
  description,
  serviceType,
  serviceUrl = `${SITE_URL}/get-started`,
  areaServed = NEW_YORK_STATE,
  audience = NEW_YORK_AUDIENCE,
  availableLanguage = ['English', 'Spanish'],
  about = null,
  overrides = {},
}) {
  return {
    '@type': THERAPY_SERVICE_TYPES,
    '@id': `${pageUrl}#service`,
    name,
    url: pageUrl,
    description,
    serviceType,
    provider: { '@id': ORG_ID },
    areaServed,
    audience,
    ...(about && { about: buildMedicalConditions(about) }),
    availableChannel: {
      ...buildTelehealthChannel(serviceUrl),
      availableLanguage,
    },
    ...overrides,
  };
}

// Conditions a service page treats, as MedicalCondition entities. Health answer
// engines reconcile condition queries against this node type, not Thing.
export function buildMedicalConditions(conditions) {
  const list = (Array.isArray(conditions) ? conditions : [conditions]).map((condition) => {
    const node = { '@type': 'MedicalCondition', name: condition.name };
    if (condition.sameAs) node.sameAs = condition.sameAs;
    return node;
  });

  return list.length === 1 ? list[0] : list;
}

function buildCatalogOffer(service) {
  return {
    '@type': 'Offer',
    '@id': `${service.url}#offer`,
    availability: 'https://schema.org/InStock',
    eligibleRegion: NEW_YORK_STATE,
    seller: { '@id': ORG_ID },
    ...(service.price && {
      priceSpecification: {
        '@type': 'PriceSpecification',
        priceCurrency: 'USD',
        minPrice: service.price.minPrice,
        maxPrice: service.price.maxPrice,
      },
    }),
    itemOffered: buildTherapyServiceSchema({ pageUrl: service.url, ...service }),
  };
}

function toAbsoluteUrl(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${SITE_URL}${url.startsWith('/') ? url : `/${url}`}`;
}

function getImageMimeType(url) {
  if (!url) return null;
  if (url.endsWith('.webp')) return 'image/webp';
  if (url.endsWith('.png')) return 'image/png';
  if (url.endsWith('.jpg') || url.endsWith('.jpeg')) return 'image/jpeg';
  return null;
}

export function buildSocialImage({ primary, fallback = null, alt = DEFAULT_SOCIAL_IMAGE_ALT, structuredDataUrl = null }) {
  const primaryUrl = toAbsoluteUrl(primary);
  const fallbackUrl = toAbsoluteUrl(fallback);
  const normalizedFallback = fallbackUrl && fallbackUrl !== primaryUrl ? fallbackUrl : null;

  return {
    primary: primaryUrl,
    primaryType: getImageMimeType(primaryUrl),
    fallback: normalizedFallback,
    fallbackType: getImageMimeType(normalizedFallback),
    twitter: normalizedFallback || primaryUrl,
    alt,
    structuredData: toAbsoluteUrl(structuredDataUrl) || normalizedFallback || primaryUrl,
  };
}

export function buildPngFallbackSocialImage(primary, alt = DEFAULT_SOCIAL_IMAGE_ALT, options = {}) {
  const fallback =
    options.fallback || (primary.endsWith('.webp') ? primary.replace(/\.webp$/, '.png') : null);

  return buildSocialImage({
    primary,
    fallback,
    alt,
    structuredDataUrl: options.structuredDataUrl,
  });
}

export function buildSocialImageMeta(image) {
  const meta = [];

  // Facebook and LinkedIn do not support WebP for link previews and use the
  // FIRST og:image they find. Advertise a single raster (PNG/JPEG) image to
  // social scrapers: prefer whichever of primary/fallback is a raster, and only
  // fall back to the primary if no raster is available. WebP is never emitted in
  // OG/Twitter meta — it has no value there (scrapers ignore it) and breaks
  // previews when it lands first. Browsers still get WebP via on-page <picture>.
  const candidates = [
    { url: image.primary, type: image.primaryType },
    { url: image.fallback, type: image.fallbackType },
  ].filter((candidate) => candidate.url);

  const isRaster = (type) => type === 'image/png' || type === 'image/jpeg';
  const ogImage = candidates.find((candidate) => isRaster(candidate.type)) || candidates[0];

  if (ogImage) {
    meta.push({ key: 'og-image', property: 'og:image', content: ogImage.url });
    if (ogImage.type) {
      meta.push({ key: 'og-image-type', property: 'og:image:type', content: ogImage.type });
    }
  }

  if (image.alt) {
    meta.push({ key: 'og-image-alt', property: 'og:image:alt', content: image.alt });
  }

  if (image.twitter) {
    meta.push({ key: 'twitter-image', name: 'twitter:image', content: image.twitter });
  }

  if (image.alt) {
    meta.push({ key: 'twitter-image-alt', name: 'twitter:image:alt', content: image.alt });
  }

  return meta;
}

export const FAVICON_SOCIAL_IMAGE = buildSocialImage({
  primary: '/images/peace-love-wellness-social.png',
  alt: 'Peace Love Wellness — Therapy in New York',
});

export function buildImageObject(url) {
  return {
    '@type': 'ImageObject',
    url,
  };
}

function buildKnownThing(topic) {
  if (typeof topic !== 'string') {
    const thing = {
      '@type': 'Thing',
      name: topic.name,
    };

    if (topic.sameAs) {
      thing.sameAs = topic.sameAs;
    }

    return thing;
  }

  const thing = {
    '@type': 'Thing',
    name: topic,
  };
  const sameAs = KNOWS_ABOUT_SAME_AS[topic.toLowerCase()];

  if (sameAs) {
    thing.sameAs = sameAs;
  }

  return thing;
}

function buildKnowsAboutThings(topics) {
  const uniqueTopics = new Map();

  topics.forEach((topic) => {
    const name = typeof topic === 'string' ? topic : topic?.name;
    if (!name) return;
    uniqueTopics.set(name.toLowerCase(), buildKnownThing(topic));
  });

  return [...uniqueTopics.values()];
}

function buildClinicianPersonFields({ profile, pageUrl, includeMainEntityOfPage = false }) {
  const person = {
    '@type': 'Person',
    '@id': `${pageUrl}#person`,
    name: profile.fullName,
    givenName: profile.firstName,
    url: pageUrl,
    image: `${SITE_URL}${profile.image}`,
    description: profile.seoDescription,
    jobTitle: profile.schema?.jobTitle || profile.roleLabel,
    hasOccupation: {
      '@type': 'Occupation',
      name: profile.schema?.jobTitle || profile.roleLabel,
    },
    worksFor: { '@id': ORG_ID },
    memberOf: { '@id': ORG_ID },
    areaServed: (profile.schema?.areaServedStates || ['New York']).map((name) => ({ '@type': 'State', name })),
  };

  if (includeMainEntityOfPage) {
    person.mainEntityOfPage = { '@id': `${pageUrl}#profilepage` };
  }

  if (profile.phone) {
    person.telephone = profile.phone;
  }

  const allKnowsAbout = [
    ...(profile.schema?.knowsAbout || []),
    ...(profile.worksWith || []),
  ];
  if (allKnowsAbout.length) {
    person.knowsAbout = buildKnowsAboutThings(allKnowsAbout);
  }

  const knowsLanguage = profile.schema?.knowsLanguage || profile.languages;

  if (knowsLanguage?.length) {
    person.knowsLanguage = knowsLanguage;
  }

  if (profile.schema?.credentials?.length) {
    person.hasCredential = profile.schema.credentials.map((credential) => ({
      '@type': 'EducationalOccupationalCredential',
      name: credential.name,
      credentialCategory: credential.category,
    }));
    person.honorificSuffix = profile.schema.credentials.map((c) => c.name).join(', ');
  }

  if (profile.schema?.alumniOf?.length) {
    person.alumniOf = profile.schema.alumniOf.map(buildEducationalOrganization);
  }

  if (profile.schema?.sameAs?.length) {
    person.sameAs = profile.schema.sameAs;
  }

  // License / NPI numbers — the strongest disambiguation signal a clinician
  // Person node can carry. Only values already published by the clinician.
  if (profile.schema?.identifier?.length) {
    person.identifier = profile.schema.identifier.map((id) => ({
      '@type': 'PropertyValue',
      propertyID: id.propertyID,
      value: id.value,
    }));
  }

  return person;
}

function buildEducationalOrganization(school) {
  if (typeof school === 'string') {
    return {
      '@type': 'EducationalOrganization',
      name: school,
    };
  }

  const organization = {
    '@type': 'EducationalOrganization',
    name: school.name,
  };

  if (school.alternateName) {
    organization.alternateName = school.alternateName;
  }

  if (school.parentOrganization) {
    organization.parentOrganization = {
      '@type': 'EducationalOrganization',
      name: school.parentOrganization,
    };
  }

  return organization;
}

export function buildPracticeEmployeeSchemas() {
  return Object.values(clinicianProfiles).map((profile) => buildClinicianPersonFields({
    profile,
    pageUrl: `${SITE_URL}/our-clinicians/${profile.slug}`,
  }));
}

export function buildPracticeEmployeeRefs() {
  return Object.values(clinicianProfiles).map((profile) => ({
    '@type': 'Person',
    '@id': `${SITE_URL}/our-clinicians/${profile.slug}#person`,
    name: profile.fullName,
    url: `${SITE_URL}/our-clinicians/${profile.slug}`,
  }));
}

export function buildPracticeAvailableServiceRefs() {
  return PRACTICE_SERVICE_CATALOG.map((service) => ({
    '@type': THERAPY_SERVICE_TYPES,
    '@id': `${service.url}#service`,
    name: service.name,
    url: service.url,
  }));
}

export function buildPracticeOrganization(overrides = {}, options = {}) {
  const {
    includeEmployees = false,
    includeAvailableServices = false,
  } = options;

  const organization = {
    '@type': ['MedicalClinic', 'MedicalBusiness', 'ProfessionalService', 'Organization'],
    '@id': ORG_ID,
    name: PRACTICE_NAME,
    legalName: PRACTICE_NAME,
    alternateName: PRACTICE_BRAND_NAME,
    url: `${SITE_URL}/`,
    logo: {
      '@type': 'ImageObject',
      '@id': `${SITE_URL}/#logo`,
      url: PRACTICE_LOGO_URL,
    },
    image: { '@id': `${SITE_URL}/#logo` },
    email: PRACTICE_CONTACT_EMAIL,
    telephone: PRACTICE_PHONE,
    description: PRACTICE_DESCRIPTION,
    slogan: 'Relational, trauma-informed online therapy in New York.',
    medicalSpecialty: 'Mental health counseling',
    founder: { '@id': `${SITE_URL}/our-clinicians/cameron-eshgh#person` },
    employee: buildPracticeEmployeeRefs(),
    availableService: buildPracticeAvailableServiceRefs(),
    address: {
      '@type': 'PostalAddress',
      streetAddress: '159 W 25th St Fl 4',
      addressLocality: 'New York',
      addressRegion: 'NY',
      postalCode: '10001',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: PRACTICE_GEO.latitude,
      longitude: PRACTICE_GEO.longitude,
    },
    hasMap: PRACTICE_GOOGLE_MAPS_URL,
    identifier: [
      {
        '@type': 'PropertyValue',
        propertyID: 'Google Business Profile CID',
        value: PRACTICE_GOOGLE_BUSINESS_CID,
      },
    ],
    areaServed: [
      { '@type': 'City', name: 'New York City' },
      NEW_YORK_STATE,
    ],
    sameAs: PRACTICE_SAME_AS,
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'intake',
        telephone: PRACTICE_PHONE,
        email: PRACTICE_CONTACT_EMAIL,
        availableLanguage: ['English', 'Spanish'],
        areaServed: NEW_YORK_STATE,
      },
    ],
    knowsAbout: [
      ...PRACTICE_KNOWS_ABOUT,
      ...PRACTICE_INSURANCE_NETWORKS.map((network) => `${network} therapy in New York`),
    ],
    keywords: PRACTICE_KEYWORDS,
    availableLanguage: ['English', 'Spanish'],
    paymentAccepted: 'Insurance, Private Pay',
    priceRange: `$${OVERALL_RANGE[0]}-$${OVERALL_RANGE[1]}`,
  };

  if (includeEmployees) {
    organization.employee = buildPracticeEmployeeSchemas();
  }

  if (includeAvailableServices) {
    organization.availableService = buildPracticeAvailableServiceRefs();
  }

  return {
    ...organization,
    ...overrides,
  };
}

export function buildPracticeOfferCatalog() {
  return {
    '@type': 'OfferCatalog',
    '@id': `${SITE_URL}/#therapy-services`,
    name: 'Therapy Services',
    itemListElement: PRACTICE_SERVICE_CATALOG.map(buildCatalogOffer),
  };
}

export function buildWebsiteSchema(overrides = {}) {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: `${SITE_URL}/`,
    name: PRACTICE_BRAND_NAME,
    publisher: { '@id': ORG_ID },
    about: { '@id': ORG_ID },
    hasPart: PRACTICE_PRIMARY_PAGES.map((page) => ({
      '@type': 'WebPage',
      name: page.name,
      url: page.url,
    })),
    potentialAction: {
      '@type': 'ContactAction',
      target: `${SITE_URL}/get-started`,
    },
    inLanguage: 'en-US',
    ...overrides,
  };
}

export function buildWebPageSchema({
  pageUrl,
  name,
  description,
  dateModified,
  breadcrumbId,
  primaryImage,
  type = 'WebPage',
  overrides = {},
}) {
  const pageNode = {
    '@type': type,
    '@id': `${pageUrl}#webpage`,
    url: pageUrl,
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
    inLanguage: 'en-US',
  };

  if (name) pageNode.name = name;
  if (description) pageNode.description = description;
  if (dateModified) pageNode.dateModified = dateModified;
  if (breadcrumbId) pageNode.breadcrumb = { '@id': breadcrumbId };
  if (primaryImage) pageNode.primaryImageOfPage = buildImageObject(primaryImage);

  return {
    ...pageNode,
    ...overrides,
  };
}

export function buildBreadcrumbSchema({ breadcrumbId, itemListElement }) {
  return {
    '@type': 'BreadcrumbList',
    '@id': breadcrumbId,
    itemListElement,
  };
}

export function buildClinicianPersonSchema({ profile, pageUrl }) {
  return buildClinicianPersonFields({ profile, pageUrl, includeMainEntityOfPage: true });
}
